// Read / clear book for the header bell. The bell's rows are derived from
// live review items, down instances, and in-flight provisions — there is no
// notification document to update. This book remembers, per person and org,
// which occurrence they have read or dismissed.
//
// A stamp identifies the occurrence. The same row id with a new stamp (a
// new incident, a new provision job) shows again. Clearing hides the row;
// it does not decide the review or heal the instance.

export const emptyBook = () => ({ read: {}, cleared: {} });

export function loadBook(raw) {
  if (!raw || typeof raw !== 'object') return emptyBook();
  const map = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? { ...v } : {});
  return { read: map(raw.read), cleared: map(raw.cleared) };
}

export function reviewStamp(item) {
  return String(item?.createdAt || item?.id || '');
}

export function healthStamp(app) {
  return String(app?.monitor?.openIncidentId || 'down');
}

export function deployStamp(app) {
  return String(app?.provisionJobId || app?.createdAt || app?.id || '');
}

function matches(map, ev) {
  return map[ev.id] === ev.stamp;
}

// Drop marks whose id left a loaded feed, or whose stamp no longer matches,
// so a later occurrence is unread and the book does not grow without bound.
// Callers must not prune against a feed that has not loaded yet: an empty
// list then deletes every saved clear, and a refresh brings the rows back.
export function settleBook(book, events, ready) {
  if (!ready) return book || emptyBook();
  return prune(book, events);
}

export function prune(book, events) {
  const live = new Map((events || []).map((e) => [e.id, e.stamp]));
  const keep = (map) => {
    const out = {};
    for (const [id, stamp] of Object.entries(map || {})) {
      if (live.get(id) === stamp) out[id] = stamp;
    }
    return out;
  };
  return { read: keep(book?.read), cleared: keep(book?.cleared) };
}

export function applyBook(events, book) {
  const listed = [];
  let unread = 0;
  for (const ev of events || []) {
    if (matches(book?.cleared || {}, ev)) continue;
    const read = matches(book?.read || {}, ev);
    if (!read) unread += 1;
    listed.push({ ...ev, read });
  }
  return { listed, unread };
}

export function markRead(book, targets, live) {
  const read = { ...(book?.read || {}) };
  const cleared = book?.cleared || {};
  for (const ev of targets || []) {
    if (cleared[ev.id] === ev.stamp) continue;
    read[ev.id] = ev.stamp;
  }
  return prune({ read, cleared: { ...cleared } }, live);
}

export function markCleared(book, targets, live) {
  const read = { ...(book?.read || {}) };
  const cleared = { ...(book?.cleared || {}) };
  for (const ev of targets || []) {
    cleared[ev.id] = ev.stamp;
    read[ev.id] = ev.stamp;
  }
  return prune({ read, cleared }, live);
}
